<script setup lang="ts">
import ConsumerProgramCard from "@/consumer/components/consumer-programs/ConsumerProgramCard.vue";
import Customer from "@/customer/interfaces/Customer";
import Beneficiary from "@/customer/interfaces/Beneficiary";
import ConsumerProgramInclusion from "@/consumer/components/consumer-programs/ConsumerProgramInclusion.vue";
import {AnimatePresence, motion} from "motion-v";
import {computed, onMounted, ref} from "vue";
import {usePatientStore} from "@/stores/patient.store";
import {ProgramWithConsumer} from "@/consumer/components/consumer-programs/ConsumerPrograms.vue";
import ConsumerProgramFeedbackShow from "@/consumer/components/consumer-programs/ConsumerProgramHideSwitch.vue";
import EmulateDetectionButton from "@/customer/components/emulate-detection-button/EmulateDetectionButton.vue";

const patientStore = usePatientStore();

const props = defineProps<{ consumer: Beneficiary | Customer, programs: ProgramWithConsumer[], customer: Customer }>();

/**
 * Program manual inclusions that have been removed via the trash icons.
 */
const trashedPrograms = ref<number[]>([]);

const hiddenProgramCount = computed(() => {
    return props.programs.filter(program => program.feedbacks?.find(feedback => feedback.hide_notification)).length;
});

/**
 * Filter the programs to only show those that are not hidden or if hidden programs are shown via option.
 */
const visiblePrograms = computed(() => {
    return props.programs.filter(program => {
        return !program.feedbacks?.find(feedback => feedback.hide_notification) || showHiddenPrograms.value;
    });
});

/**
 * The programs actually drawn: the visible ones, minus those trashed since the page was opened.
 *
 * A trashed program stays in the props until the store snapshot comes back, so this is the only
 * reliable answer to "is there anything left to show".
 */
const renderedPrograms = computed(() => {
    return visiblePrograms.value.filter(program => !trashedPrograms.value.includes(program.id));
});

/**
 * The row heading: an invitation to talk about the programs, or the consumer name alone
 * when there is nothing to talk about yet.
 */
const heading = computed(() => {
    const firstName = props.consumer.information?.first_name;

    return `Parlez-en à ${firstName}`;
});

const showHiddenPrograms = ref<boolean>(false);

onMounted(() => {
    window.ipcRenderer.on('program-hide', (_: Event, consumerKey: string, programId: number) => {
        if (props.consumer.consumer_key == consumerKey) {
            trashedPrograms.value.push(programId);
        }
    });
});

/**
 * Remove a program from the list of programs.
 * @param programId
 */
const removeProgram = (programId: number) => {
    trashedPrograms.value.push(programId);
    patientStore.refresh();
};

/**
 * Show the program again if it has been removed by a previous action.
 * @param programId
 */
const unRemoveProgram = (programId: number) => {
    trashedPrograms.value = trashedPrograms.value.filter(id => id !== programId);
};

</script>

<template>
  <div class="flex justify-between flex-wrap mb-4 pr-6 gap-3">
    <div class="flex gap-4 items-center">
      <h1 class="text-gray-dark">
        {{ heading }}
      </h1>
      <div class="flex flex-1 pr-4 gap-2">
        <ConsumerProgramInclusion :consumer="consumer" @program-added="unRemoveProgram" />
        <ConsumerProgramFeedbackShow
          v-if="hiddenProgramCount > 0"
          v-model:enabled="showHiddenPrograms"
          :count="hiddenProgramCount"
        />
      </div>
    </div>
    <EmulateDetectionButton class="self-start" :customer="customer" :consumer="consumer" />
  </div>
  <div>
    <div class="flex flex-1 flex-row space-y-2 overflow-x-auto pb-3">
      <div
        v-if="renderedPrograms.length > 0"
        class="flex flex-1 space-x-5"
      >
        <div v-for="program in visiblePrograms" :key="`program-${program.id}-${consumer.id}`">
          <AnimatePresence>
            <motion.div
              v-if="!trashedPrograms.includes(program.id)"
              :key="'program' + program.id"
              :initial="{ opacity: 1, x: 0 }"
              :exit="{ opacity: 0, y: 100 }"
              :transition="{ duration: 0.25, type: 'tween' }"
            >
              <ConsumerProgramCard
                :program="program"
                :consumer="consumer"
                :customer="customer"
                @trash-program="removeProgram(program.id)"
              />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
      <div
        v-else
        class="text-sm"
      >
        Aucun programme pour le moment.
      </div>
    </div>
  </div>
</template>
